import * as THREE from 'three';
import { WIND, SEA, SITE } from '../config.js';

const STORE_KEY = 'sofatech:boat';

// ---------------------------------------------------------------------------
// Sailing the tjalk.
//
// Enough of a sailing model to feel like one and no more: the wind blows from
// a fixed quarter, you steer with the arrow keys, and how fast you go depends
// on your angle to it. Beating upwind is slow but never impossible — being
// genuinely stuck in irons is realistic and infuriating, and this is a
// website.
// ---------------------------------------------------------------------------

export class Sailing {
    /** @param ground from createGround — every island, not just the one. */
    constructor(boat, ground, { voxelSize }) {
        this.boat = boat;
        this.polder = ground;
        this.voxelSize = voxelSize;
        this.worldSpan = (ground.size || 0) * voxelSize;

        this.heading = WIND.direction + Math.PI * 0.35;   // start on a reach
        this.speed = 0;
        this.turn = 0;          // -1, 0, +1 from the keyboard
        this.heel = 0;
        this.setback = 0;
        this.underway = false;  // true once the visitor has taken the tiller
        this.frozen = false;    // lying to, with the nets down

        this.position = new THREE.Vector3();
        this._probe = new THREE.Vector2();
        this._sinceSave = 0;
        // Solid things that are not terrain — the jetty, so far.
        this.obstacles = [];
        // Other people's hulls, refreshed every frame from the fleet. Circles
        // rather than the real shape: a tjalk is long, but two boats bouncing
        // off a generous circle reads better than one slicing through another
        // because the maths said the corners missed.
        this.hulls = [];
    }

    /**
     * Put her out to sea, already sailing, pointed at a particular island —
     * the Zaanse Schans, for a first-time visitor, because that is where the
     * links are. They arrive by water rather than being handed a diorama, and
     * the bearing is derived from the wind so the approach is always on a
     * decent point of sail, whatever the wind is set to.
     *
     * Not on the canal: the Zaan is four world units across and the hull is
     * nearly seven long, so a tjalk could no more turn in it than a lorry
     * could turn in a corridor.
     */
    placeApproaching(target, radius) {
        // Steer to pass the island rather than at it. Aimed dead at the middle
        // she simply runs ashore a few seconds after the page opens; offset,
        // she closes the coast, sails along it, and waits for someone to take
        // the tiller.
        //
        // The bearing is scattered so two visitors do not arrive down the same
        // line, but only within about fifty degrees of the wind's best angle:
        // a random bearing would sometimes be dead upwind, and a first
        // impression of crawling is a poor one.
        const spread = (Math.random() - 0.5) * 1.7;
        const approach = WIND.direction + 0.8 + spread;
        const bearing = approach + Math.PI;
        this._lateral = 14 + Math.random() * 8;

        let r = radius * (0.86 + Math.random() * 0.3);
        // Should be open water this far out, but never start her aground.
        for (let i = 0; i < 40; i++) {
            const x = target.x + Math.sin(bearing) * r;
            const z = target.z + Math.cos(bearing) * r;
            if (this.isFloatable(x, z)) break;
            r -= this.voxelSize * 4;
        }

        const px = target.x + Math.sin(bearing) * r;
        const pz = target.z + Math.cos(bearing) * r;
        this.position.set(px, this.polder.seaLevel * this.voxelSize, pz);

        // Aim at a mark offset from the island's middle, not the middle
        // itself: aimed dead at it she runs ashore seconds after the page
        // opens.
        const tx = target.x + Math.cos(bearing) * this._lateral;
        const tz = target.z - Math.sin(bearing) * this._lateral;
        this.heading = Math.atan2(tx - px, tz - pz);
        this.underway = true;      // already making way when the page opens
        this.speed = WIND.strength * 0.55;
        this._apply(0);
    }

    /**
     * Put her back where she was left — but only if she was left in this
     * world. Position and heading only: the flag belongs to the relay, and a
     * colour remembered here would fight whatever it hands out next time.
     *
     * @returns 'none' | 'restored'
     */
    restore() {
        let saved;
        try {
            saved = JSON.parse(localStorage.getItem(STORE_KEY) || 'null');
        } catch {
            return 'none';   // storage off, or somebody put nonsense in it
        }
        if (!saved) return 'none';

        // A berth saved before the ground moved is not a berth, it is a
        // coordinate. Start again as a new visitor does.
        if (saved.v !== SITE.worldVersion) return 'none';

        const { x, z, h } = saved;
        if (![x, z, h].every((n) => typeof n === 'number' && Number.isFinite(n))) return 'none';
        // Cheap last check: the version is right, so this should be water.
        if (!this.isFloatable(x, z, { hulls: false })) return 'none';

        this.position.set(x, this.polder.seaLevel * this.voxelSize, z);
        this.heading = h;
        this.underway = true;
        this.speed = WIND.strength * 0.4;
        this._apply(0);
        return 'restored';
    }

    /** Remember where she is, so a refresh does not send her back to sea. */
    _remember(dt) {
        this._sinceSave += dt;
        if (this._sinceSave < 1) return;    // once a second is plenty
        this._sinceSave = 0;
        try {
            localStorage.setItem(STORE_KEY, JSON.stringify({
                v: SITE.worldVersion,
                x: +this.position.x.toFixed(2),
                z: +this.position.z.toFixed(2),
                h: +this.heading.toFixed(3),
            }));
        } catch { /* nothing worth breaking for */ }
    }

    /**
     * Is there water — not land, not the bank, not the shoal — here?
     *
     * @param opts.hulls false to ignore other boats. Being asked to move
     *   because somebody drifted alongside is not the same as being aground.
     */
    isFloatable(wx, wz, { hulls = true, built = true } = {}) {
        // The ring of shallows that closes off the sea room. Same wobble as
        // the water shader draws, so she grounds exactly where the foam is.
        const r = Math.hypot(wx, wz);
        const a = Math.atan2(wz, wx);
        // Identical to the shader's wobble, scaled the same way, so she
        // grounds exactly where the foam is and not a few units off it.
        const scale = SEA.shoalRadius * 0.04;
        const wobble = (Math.sin(a * 3) * 1.6 + Math.sin(a * 7 + 1.1) * 0.9) * scale;
        if (r > SEA.shoalRadius + wobble - 1.2) return false;

        // Anything built out over the water. A pier is a line, not a circle,
        // so the test is distance to the segment rather than to a point.
        for (const o of built ? this.obstacles : []) {
            const dx = o.bx - o.ax;
            const dz = o.bz - o.az;
            const len2 = dx * dx + dz * dz || 1;
            let t = ((wx - o.ax) * dx + (wz - o.az) * dz) / len2;
            t = Math.max(0, Math.min(1, t));
            const px = o.ax + dx * t;
            const pz = o.az + dz * t;
            if ((wx - px) ** 2 + (wz - pz) ** 2 < o.r * o.r) return false;
        }

        // Another boat is something to go round, not through.
        for (const h of hulls ? this.hulls : []) {
            if ((wx - h.x) ** 2 + (wz - h.z) ** 2 < h.r * h.r) return false;
        }

        // Ask the island itself: it knows where its own grid sits in the sea.
        // Anywhere no island covers is open water, which is very floatable.
        const { polder } = this;
        if (!polder.covers(wx, wz)) return true;
        return polder.topAtWorld(wx, wz) < polder.seaLevel;
    }

    update(dt) {
        const { boat } = this;

        // --- lying to ---
        // While the nets are down she neither steers nor makes way; the sail
        // is spilled rather than the boat teleported to a stop.
        if (this.frozen) {
            this.speed += (0 - this.speed) * Math.min(1, dt * 2.2);
            this.position.x += Math.sin(this.heading) * this.speed * dt;
            this.position.z += Math.cos(this.heading) * this.speed * dt;
            this._apply(dt);
            return;
        }

        // --- steering ---
        if (this.turn !== 0) this.underway = true;
        this.heading += this.turn * WIND.turnRate * dt;

        // --- the tide, which would rather you stayed ---
        // Open sea in every direction means you can sail out until the island
        // is lost in the fog and there is nothing left to look at. Past a
        // certain offing the flood sets you back.
        //
        // It sets the BOAT back, and never touches the heading. Steering her
        // home instead means chasing a bearing that moves as she travels, so
        // she can never settle on it: the helm creeps round and round and the
        // wake comes out as a drifting spiral. The tiller belongs to the
        // visitor — the tide is only allowed to push.
        const offing = Math.hypot(this.position.x, this.position.z);
        this.setback = offing > WIND.offing
            ? Math.min(1, (offing - WIND.offing) / 25)
            : 0;

        // --- how well the sail is drawing ---
        // +1 running with the wind dead astern, -1 pointing straight into it.
        const alignment = Math.cos(this.heading - WIND.direction);
        const drive = 0.22 + 0.78 * (alignment * 0.5 + 0.5);
        const target = this.underway ? WIND.strength * drive : 0;
        this.speed += (target - this.speed) * Math.min(1, dt * 0.9);

        // --- move, refusing to sail up the bank ---
        const step = this.speed * dt;
        if (this.setback > 0) {
            // Carried bodily towards the island, hardest the further out she
            // is. Sail on regardless and you simply stop making ground.
            const home = Math.hypot(this.position.x, this.position.z) || 1;
            const drift = WIND.setback * this.setback * dt;
            this.position.x -= (this.position.x / home) * drift;
            this.position.z -= (this.position.z / home) * drift;
        }
        const fx = Math.sin(this.heading);
        const fz = Math.cos(this.heading);
        // Probe ahead of the bow, not at the centre, or she runs aground with
        // half the hull already over the grass.
        const look = boat.length * 0.55;
        const nx = this.position.x + fx * step;
        const nz = this.position.z + fz * step;
        if (this.isFloatable(nx + fx * look, nz + fz * look)) {
            this.position.x = nx;
            this.position.z = nz;
        } else if (this.isFloatable(this.position.x + fx * look, nz + fz * look)) {
            this.position.z = nz;            // slide along the bank
        } else if (this.isFloatable(nx + fx * look, this.position.z + fz * look)) {
            this.position.x = nx;
        } else {
            this.speed *= 0.25;              // nosed into the quay
        }

        this._apply(dt);
        this._remember(dt);
    }

    _apply(dt) {
        const { boat } = this;
        const time = performance.now() / 1000;

        boat.group.position.copy(this.position);
        boat.group.rotation.y = this.heading;

        // Heel away from the wind, hardest when it is on the beam.
        const across = Math.sin(this.heading - WIND.direction);
        const wantHeel = -across * 0.16 * (this.speed / Math.max(WIND.strength, 0.001));
        this.heel += (wantHeel - this.heel) * Math.min(1, (dt || 0.016) * 2.5);
        boat.group.rotation.z = this.heel;

        // Bob, and a little pitch as she drives.
        boat.group.position.y += Math.sin(time * 1.15) * 0.05;
        boat.group.rotation.x = Math.sin(time * 0.83) * 0.018 - this.speed * 0.012;

        // Sheet the sail: square out when running, hauled in when beating.
        const relative = wrap(WIND.direction - this.heading);
        const sheeted = THREE.MathUtils.clamp(relative, -1.25, 1.25);
        boat.rigPivot.rotation.y += (sheeted - boat.rigPivot.rotation.y)
            * Math.min(1, (dt || 0.016) * 1.6);
    }
}

/** Fold an angle into -PI..PI. */
function wrap(a) {
    return Math.atan2(Math.sin(a), Math.cos(a));
}
